/**
 * ToleranceMetricsView (P402.1). Route: /twin/tolerance-metrics.
 *
 * Every declared tolerance dimension, evaluated live against the same
 * projected outage duration the recovery model produces.
 *
 * Each metric shows its SOURCE. "Where is this number from" is the
 * question that separates a tolerance from a guess, and it is the half
 * of the question a form-based tool cannot answer.
 */
import { useState } from "react";
import { Bdi } from "shared/components/Bdi";
import { Explainer } from "shared/components/Explainer";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { ToleranceSigners } from "shared/tolerance/ToleranceSigners";
import { byIbsId, useToleranceState } from "shared/tolerance/useToleranceState";
import { toleranceSentence } from "shared/tolerance/toleranceSentence";

import { api } from "shared/api/client";
import { canRunOperations } from "shared/api/authority";
import { useMe } from "shared/api/useMe";
import { useSeatHolders } from "shared/api/useSeatHolders";
import { RecordObservationForm } from "twin/tolerance/RecordObservationForm";
import { useMyEstate } from "twin/home/useMyEstate";
import { MarsadIcon } from "shared/components/MarsadIcon";

interface MetricVerdict {
  metric: string;
  label: string;
  unit: string;
  source: string;
  threshold: string | null;
  projected: string | null;
  breached: boolean | null;
  reason: string;
  /** P429.3: the worst recorded value, for an integrity measure. */
  observed?: string | null;
  /** P429.3: "observed" for the integrity measures, never projected. */
  basis?: "projected" | "observed";
}

interface ToleranceRow {
  ibs_service_id: string;
  ibs_code: string;
  ibs_name: string;
  mvb_flag: boolean;
  /** Round 18 L11 -- a Critical Internal Function, whose MVB membership
   *  reads as membership of the declaration, not "MVB". */
  is_function?: boolean;
  projected_recovery_hours: number | null;
  verdict: "breached" | "indeterminate" | "within" | "undeclared";
  metrics: MetricVerdict[];
}

interface Report {
  summary: {
    assessed: number;
    /** Round 16 L3 -- functions counted apart. */
    assessed_functions?: number;
    assessed_admitted?: number;
    breached: number;
    indeterminate: number;
    multi_metric_declared: number;
    /** P429.3: breaches on a dimension other than time. */
    breached_beyond_time?: number;
  };
  rows: ToleranceRow[];
}

const VERDICT_TONE: Record<string, string> = {
  breached: "bg-rose-100 text-rose-800",
  // An unjudgeable metric is NOT good news, so it does not read green.
  indeterminate: "bg-amber-100 text-amber-800",
  within: "bg-emerald-100 text-emerald-800",
  undeclared: "bg-slate-200 text-slate-700",
};

const METRIC_TONE = (breached: boolean | null) =>
  breached === true
    ? "bg-rose-100 text-rose-800"
    : breached === false
      ? "bg-emerald-100 text-emerald-800"
      : "bg-slate-200 text-slate-700";

export function ToleranceMetricsView() {
  const { t } = useTranslation();
  // Round 18 G23 -- a pending leg names its holder.
  const seats = useSeatHolders();
  const [params] = useSearchParams();
  const focusIbs = params.get("ibs");
  /* Round 19 G41 (carried) -- one card at a time made comparing two
     services a matter of memory. Several open at once. */
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (code: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const governance = useToleranceState();
  const me = useMe();
  const operator = canRunOperations(me.data);
  /* Round 16 N6 -- the owner of a service or function records what his
     own drill or test showed; an operator records for any. The server
     holds the same line. */
  const estate = useMyEstate();
  const owned = new Set((estate.data?.ibses ?? []).map((i) => i.id));
  const canRecord = (ibsId: string) => operator || owned.has(ibsId);

  const report = useQuery({
    queryKey: ["twin", "tolerance-metrics"],
    queryFn: () => api.get<Report>("/v1/twin/tolerance/metrics"),
    refetchInterval: 60_000,
  });
  const p = report.data;
  /* Round 16 H22, tenth round -- the tile from her service landed on a
     list without it. With `?ibs=` the page is about that service: its
     row alone where something live reaches it, and its own tolerance
     where nothing does. "Show every service" returns the whole list. */
  const focusRow = focusIbs ? byIbsId(governance.data).get(focusIbs) : undefined;
  const shownRows = p
    ? focusIbs
      ? p.rows.filter((r) => r.ibs_service_id === focusIbs)
      : p.rows
    : [];

  return (
    <article data-testid="tolerance-metrics-view" className="max-w-4xl space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="tolerance-clock"
            className="me-2 inline-block h-6 w-6 align-[-4px]"
          />
          {t("toleranceMetrics.heading")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">{t("toleranceMetrics.body")}</p>
      </header>

      <Explainer termKey="impact_tolerance" className="max-w-3xl" />

      {report.isError && (
        <p className="text-sm text-rose-700">{t("toleranceMetrics.error")}</p>
      )}

      {/* D2 -- how many tolerances are actually SIGNED FOR. The screen
          showed how many were breached without ever saying how many
          carried a signature, which is the difference between a
          governed figure and a number somebody typed. */}
      {governance.data && (
        <p
          data-testid="tolerance-governance-summary"
          className="text-xs text-slate-600"
        >
          {t("toleranceState.summary", {
            total: governance.data.total,
            admitted: governance.data.admitted_services ?? 0,
            functions: governance.data.functions ?? 0,
            admittedFunctions: governance.data.admitted_functions ?? 0,
            signed: governance.data.signed,
            unsigned: governance.data.set_unsigned,
            pending: governance.data.pending,
            undeclared: governance.data.undeclared,
          })}
        </p>
      )}

      {/* B4 -- the tile linked here and the reader's own service was not
          among the rows.
          The report assesses only IBSes a live condition actually
          reaches, which is deliberate: an IBS nothing has touched has
          no projection, and counting silence as headroom is how a
          dashboard reassures you into an outage. But arriving from an
          IBS and finding twelve other rows is a dead end, so the page
          says what is true about the one she came for. */}
      {p && focusIbs && (
        <section
          data-testid="tolerance-metrics-focus"
          className="space-y-1 rounded border border-paper-2 bg-white px-3 py-2 text-xs text-slate-600"
        >
          {focusRow && (
            <p className="text-sm text-slate-800">
              <Link
                to={`/twin/ibs/${focusRow.ibs_service_id}`}
                className="font-medium text-brand-brassDark hover:underline"
              >
                <Bdi>{focusRow.ibs_name}</Bdi>
              </Link>
              {toleranceSentence(t, focusRow, seats.data) && (
                <span data-testid="tolerance-metrics-focus-state">
                  {" \u00b7 "}
                  {toleranceSentence(t, focusRow, seats.data)}
                </span>
              )}
            </p>
          )}
          {!p.rows.some((r) => r.ibs_service_id === focusIbs) && (
            <p data-testid="tolerance-metrics-focus-absent">
              {t("toleranceMetrics.focusNotReached")}
            </p>
          )}
          <Link
            to="/twin/tolerance-metrics"
            data-testid="tolerance-metrics-show-all"
            className="text-brand-brassDark hover:underline"
          >
            {t("toleranceMetrics.showAll", { count: p.rows.length })}
          </Link>
        </section>
      )}

      {p && (
        <>
          <section
            data-testid="tolerance-metrics-summary"
            className="flex flex-wrap gap-2 text-sm"
          >
            <span className="rounded border border-paper-2 bg-white px-2 py-1">
              {t("toleranceMetrics.assessed", { count: p.summary.assessed })}
              {/* Round 17 L6 -- how many of them are on the register. */}
              {p.summary.assessed_admitted !== undefined &&
                p.summary.assessed_admitted !== p.summary.assessed &&
                t("toleranceMetrics.assessedAdmitted", {
                  count: p.summary.assessed_admitted,
                })}
              {(p.summary.assessed_functions ?? 0) > 0 &&
                t("toleranceMetrics.assessedFunctions", {
                  count: p.summary.assessed_functions ?? 0,
                })}
            </span>
            <span className="rounded bg-rose-100 px-2 py-1 text-rose-800">
              {p.summary.breached} {t("toleranceMetrics.breached")}
            </span>
            {(p.summary.breached_beyond_time ?? 0) > 0 && (
              <span
                data-testid="tolerance-metrics-beyond-time"
                className="rounded bg-rose-50 px-2 py-1 text-rose-800"
              >
                {t("toleranceMetrics.beyondTime", {
                  count: p.summary.breached_beyond_time ?? 0,
                })}
              </span>
            )}
            {p.summary.indeterminate > 0 && (
              <span
                data-testid="tolerance-metrics-indeterminate"
                className="rounded bg-amber-100 px-2 py-1 text-amber-800"
              >
                {p.summary.indeterminate} {t("toleranceMetrics.indeterminate")}
              </span>
            )}
            <span className="rounded bg-paper-2 px-2 py-1 text-slate-600">
              {t("toleranceMetrics.multiMetric", {
                count: p.summary.multi_metric_declared,
              })}
            </span>
          </section>

          <ul className="space-y-1.5">
            {shownRows.map((r) => {
              const declared = r.metrics.filter((m) => m.threshold !== null);
              const signers = byIbsId(governance.data).get(r.ibs_service_id);
              return (
                <li
                  key={r.ibs_service_id}
                  data-testid={`tolerance-row-${r.ibs_code}`}
                  data-verdict={r.verdict}
                  className="rounded border border-paper-2 bg-white p-2.5"
                >
                  {/* Round 20 Y5 -- who signed it, on the page. */}
                  {signers && <ToleranceSigners row={signers} />}
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span
                      className={`rounded px-1.5 py-0.5 text-xs ${VERDICT_TONE[r.verdict]}`}
                    >
                      {t(`toleranceMetrics.verdict.${r.verdict}`)}
                    </span>
                    {/* C7 -- the row named the IBS and refused to open
                        it, while the button two lines down linked to the
                        same place. A row that names an entity links. */}
                    <Link
                      to={`/twin/ibs/${r.ibs_service_id}`}
                      data-testid={`tolerance-ibs-link-${r.ibs_service_id}`}
                      className="font-medium text-brand-brassDark hover:underline"
                    >
                      <Bdi>{r.ibs_name}</Bdi>
                    </Link>
                    {r.mvb_flag && (
                      <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs text-indigo-800">
                        {r.is_function
                          ? t("mvbMembers.inDeclaration")
                          : t("toleranceMetrics.mvb")}
                      </span>
                    )}
                    <span className="text-xs tabular-nums text-slate-500">
                      {r.projected_recovery_hours !== null
                        ? t("toleranceMetrics.projected", {
                            hours: r.projected_recovery_hours,
                          })
                        : t("toleranceMetrics.noProjection")}
                    </span>
                    <span className="text-xs text-slate-400">
                      {t("toleranceMetrics.declaredCount", {
                        count: declared.length,
                      })}
                    </span>
                    {/* A3 -- this page named the IBSes with no tolerance
                        and offered no way to give them one, so the reader
                        who came here to fix the gap had to go and find
                        the IBS by hand. The form is on its page. */}
                    {r.verdict === "undeclared" && (
                      <Link
                        to={`/twin/ibs/${r.ibs_service_id}`}
                        data-testid={`tolerance-declare-${r.ibs_code}`}
                        className="text-xs font-medium text-brand-brassDark hover:underline"
                      >
                        {t("toleranceMetrics.declareFor", { ibs: r.ibs_name })}
                      </Link>
                    )}
                  </div>

                  {declared.length > 0 && (
                    <>
                      <button
                        type="button"
                        data-testid={`tolerance-toggle-${r.ibs_code}`}
                        onClick={() => toggle(r.ibs_code)}
                        className="mt-1.5 text-xs text-brand-brassDark hover:underline"
                      >
                        {open.has(r.ibs_code)
                          ? t("toleranceMetrics.hideMetrics")
                          : t("toleranceMetrics.showMetrics")}
                      </button>
                      {open.has(r.ibs_code) && (
                        <ul
                          data-testid={`tolerance-metrics-${r.ibs_code}`}
                          className="mt-1.5 space-y-1.5"
                        >
                          {declared.map((m) => (
                            <li
                              key={m.metric}
                              data-testid={`tolerance-metric-${r.ibs_code}-${m.metric}`}
                              data-breached={String(m.breached)}
                              className="rounded bg-paper-2 p-2"
                            >
                              <div className="flex flex-wrap items-baseline gap-2">
                                <span
                                  className={`rounded px-1.5 py-0.5 text-xs ${METRIC_TONE(m.breached)}`}
                                >
                                  {m.breached === true
                                    ? t("toleranceMetrics.metricBreached")
                                    : m.breached === false
                                      ? t("toleranceMetrics.metricWithin")
                                      : t("toleranceMetrics.metricUnjudgeable")}
                                </span>
                                <span className="text-sm text-slate-900">
                                  {m.label}
                                </span>
                                {m.basis === "observed" && (
                                  <span
                                    data-testid={`tolerance-observed-basis-${m.metric}`}
                                    className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                                  >
                                    {t("toleranceMetrics.observedBasis")}
                                  </span>
                                )}
                              </div>
                              <p className="mt-0.5 text-xs text-slate-700">
                                {m.reason}
                              </p>
                              {/* The half a form-based tool cannot answer. */}
                              <p className="mt-0.5 text-xs italic text-slate-500">
                                {t("toleranceMetrics.sourceLabel")} {m.source}
                              </p>
                              {m.basis === "observed" &&
                                canRecord(r.ibs_service_id) && (
                                  <RecordObservationForm
                                    ibsId={r.ibs_service_id}
                                    metric={m.metric}
                                    unit={m.unit}
                                  />
                                )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          <p className="border-t border-paper-2 pt-3 text-xs text-slate-500">
            {t("toleranceMetrics.boundary")}
          </p>
        </>
      )}
    </article>
  );
}
